<template>
  <header class="worldmc-header">
    <div class="header-inner">
      <button class="brand" type="button" @click="goHome">
        <img
          class="brand-logo"
          src="../../../assets/worldmc/logo.png"
          alt="IWMC 2027"
        />
      </button>
      <nav class="main-nav" aria-label="Primary navigation">
        <button
          v-for="item in navItems"
          :key="item.path"
          type="button"
          :class="{ active: isActive(item.path) }"
          @click="navigate(item.path)"
        >
          {{ item.label }}
        </button>
      </nav>
      <div class="header-actions">
        <div class="language-toggle">
          <button
            type="button"
            :class="{ active: locale === 'en' }"
            @click="switchLanguage('en')"
          >
            EN
          </button>
          <span class="language-divider">/</span>
          <button
            type="button"
            :class="{ active: locale === 'zh' }"
            @click="switchLanguage('zh')"
          >
            中文
          </button>
        </div>
        <button
          v-if="!user?._id"
          type="button"
          class="register-button"
          @click="navigate('/login')"
        >
          {{ t("shell.worldmc.nav.register") }}
        </button>
        <button type="button" class="signin-button" @click="handleAccount">
          {{
            user?._id
              ? t("shell.worldmc.nav.account")
              : t("shell.worldmc.nav.signIn")
          }}
        </button>
      </div>
      <button
        class="nav-toggle"
        type="button"
        aria-label="Menu"
        @click="menuOpen = true"
      >
        <span />
        <span />
        <span />
      </button>
    </div>
    <!-- 小屏（≤768px）右侧滑入导航抽屉：仅主导航项，语言/登录仍留顶栏 -->
    <transition name="worldmc-drawer">
      <div v-if="menuOpen" class="nav-drawer-mask" @click="closeMenu">
        <nav class="nav-drawer" aria-label="Mobile navigation" @click.stop>
          <button
            class="nav-drawer-close"
            type="button"
            aria-label="Close menu"
            @click="closeMenu"
          >
            ✕
          </button>
          <button
            v-for="item in navItems"
            :key="item.path"
            type="button"
            class="nav-item"
            :class="{ active: isActive(item.path) }"
            @click="goFromDrawer(item.path)"
          >
            {{ item.label }}
          </button>
        </nav>
      </div>
    </transition>
    <!-- 公共页（订单列表/详情等）面包屑区，与设计稿「Home > Order List > Order Detail」一致 -->
    <div class="worldmc-header-bottom" v-if="showBreadcrumb">
      <div class="breadcrumb-box">
        <y-breadcrumb :routes="routes" theme="default" />
      </div>
    </div>
  </header>
</template>

<script setup>
import YBreadcrumb from "@ui/Breadcrumb/index.vue";
import { storeToRefs } from "pinia";
import { computed, onBeforeUnmount, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { useRoute, useRouter } from "vue-router";

import { useYChannelStoreHook } from "@/distributions/consumer/y/store/channel";
import { useUserStoreHook } from "@/store/modules/user";

const { t, locale } = useI18n();
const router = useRouter();
const route = useRoute();
const userStore = useUserStoreHook();
const channelStore = useYChannelStoreHook();
const { user } = storeToRefs(userStore);

defineProps({
  showBreadcrumb: {
    type: Boolean,
    default: false
  },
  routes: {
    type: Array,
    default: () => []
  }
});

/** 主导航与 channels/worldmc/config 的 configs.navigation 同源 */
const navItems = computed(() =>
  (channelStore.configs?.navigation || []).map(item => ({
    path: item.path,
    label: t(item.label)
  }))
);

const isActive = path => {
  if (path === "/index") {
    return route.path === "/index" || route.path.startsWith("/hotel");
  }
  if (path === "/travel-packages/1") {
    return route.path.startsWith("/travel-packages");
  }
  return route.path === path;
};

const navigate = path => {
  router.push({ path, query: { channel: "worldmc" } });
};

const goHome = () => navigate("/index");

const handleAccount = () => {
  navigate(user.value?._id ? "/orders" : "/login");
};

const switchLanguage = value => {
  if (locale.value !== value) {
    userStore.changeLanguage(value);
  }
};

/* 小屏导航抽屉：跨回桌面宽度自动关闭；打开期间锁定 body 滚动 */
const menuOpen = ref(false);
const mobileMedia = window.matchMedia("(max-width: 768px)");
const onMediaChange = event => {
  if (!event.matches) {
    menuOpen.value = false;
  }
};
mobileMedia.addEventListener("change", onMediaChange);

watch(menuOpen, value => {
  document.body.style.overflow = value ? "hidden" : "";
});

onBeforeUnmount(() => {
  mobileMedia.removeEventListener("change", onMediaChange);
  document.body.style.overflow = "";
});

const closeMenu = () => {
  menuOpen.value = false;
};

const goFromDrawer = path => {
  closeMenu();
  navigate(path);
};

defineOptions({ name: "WorldmcHeader" });
</script>

<style lang="scss" scoped>
/* 设计口径：深蓝 #0A4486 sticky 头部，品牌区 + 主导航 + EN/中文 + USD + 账户 */
.worldmc-header {
  background: #0a4486;
}

.header-inner {
  position: relative;
  display: flex;
  align-items: center;
  width: 100%;
  max-width: 1440px;
  height: 68px;
  padding: 0 72px;
  margin: 0 auto;
}

.brand {
  padding: 0;
  border: 0;
  background: transparent;
  cursor: pointer;
}

/* 蓝湖「image 1」Logo 切图：1440 画布下 150x43 @ (72,13) */
.brand-logo {
  display: block;
  width: 150px;
  height: 43px;
}

.main-nav {
  display: flex;
  gap: 38px;
  margin-left: 40px;
}

.main-nav button {
  position: relative;
  padding: 0;
  border: 0;
  color: rgba(255, 255, 255, 0.6);
  background: transparent;
  cursor: pointer;
  font-family: "lato", sans-serif;
  font-size: 13px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  white-space: nowrap;
}

.main-nav button:hover {
  color: #fff;
}

.main-nav button.active {
  color: #fff;
  font-weight: 700;
}

.main-nav button.active::after {
  position: absolute;
  right: -6px;
  bottom: -10px;
  left: -6px;
  height: 2px;
  background: #fff;
  content: "";
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-left: auto;
}

.language-toggle,
.language-toggle button {
  padding: 0;
  border: 0;
  color: rgba(255, 255, 255, 0.65);
  background: transparent;
  cursor: pointer;
  font-family: "lato", sans-serif;
  font-size: 14px;
}

.language-toggle {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-right: 14px;
}

.language-toggle button.active {
  color: #fff;
}

.language-divider {
  color: rgba(255, 255, 255, 0.35);
}

.register-button,
.signin-button {
  height: 30px;
  padding: 0 16px;
  border: 1px solid rgba(255, 255, 255, 0.8);
  color: #fff;
  background: transparent;
  cursor: pointer;
  font-family: "lato", sans-serif;
  font-size: 12px;
  letter-spacing: 0.05em;
  white-space: nowrap;
}

.signin-button {
  border-color: #fff;
  color: #004c82;
  background: #fff;
  font-weight: 700;
}

/* 面包屑区：白底条（仅公共页传入 showBreadcrumb 时渲染） */
.worldmc-header-bottom {
  position: relative;
  background: #fff;

  .breadcrumb-box {
    position: relative;
    z-index: 2;
    width: 1080px;
    padding: 18px 0 30px 0;
    margin: 0 auto;
  }
}

/* 汉堡按钮：桌面隐藏，≤768px 显示 */
.nav-toggle {
  display: none;
  flex-direction: column;
  justify-content: center;
  gap: 5px;
  width: 36px;
  height: 36px;
  padding: 0 8px;
  margin-left: 12px;
  border: 0;
  background: transparent;
  cursor: pointer;
}

.nav-toggle span {
  display: block;
  height: 2px;
  background: #fff;
}

/* 小屏导航抽屉：右侧滑入 + 遮罩 */
.nav-drawer-mask {
  position: fixed;
  inset: 0;
  z-index: 1500;
  background: rgba(13, 31, 43, 0.55);
}

.nav-drawer {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  display: flex;
  flex-direction: column;
  width: 280px;
  max-width: 82vw;
  padding: 64px 28px 28px;
  background: #0a4486;
  overflow-y: auto;
}

.nav-drawer .nav-item {
  padding: 14px 0;
  border: 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.12);
  color: rgba(255, 255, 255, 0.75);
  background: transparent;
  cursor: pointer;
  font-family: "lato", sans-serif;
  font-size: 14px;
  letter-spacing: 0.08em;
  text-align: left;
  text-transform: uppercase;
}

.nav-drawer .nav-item.active {
  color: #fff;
  font-weight: 700;
}

.nav-drawer-close {
  position: absolute;
  top: 14px;
  right: 14px;
  padding: 8px;
  border: 0;
  color: #fff;
  background: transparent;
  cursor: pointer;
  font-family: "lato", sans-serif;
  font-size: 16px;
  line-height: 1;
}

.worldmc-drawer-enter-active,
.worldmc-drawer-leave-active {
  transition: opacity 0.25s ease;
}

.worldmc-drawer-enter-active .nav-drawer,
.worldmc-drawer-leave-active .nav-drawer {
  transition: transform 0.25s ease;
}

.worldmc-drawer-enter-from,
.worldmc-drawer-leave-to {
  opacity: 0;
}

.worldmc-drawer-enter-from .nav-drawer,
.worldmc-drawer-leave-to .nav-drawer {
  transform: translateX(100%);
}

@media screen and (max-width: 1280px) {
  .main-nav {
    gap: 22px;
    padding: 0 20px;
  }
}

@media screen and (max-width: 1100px) {
  .header-inner {
    flex-wrap: wrap;
    height: auto;
    padding: 12px 24px;
  }

  .main-nav {
    order: 3;
    width: 100%;
    margin: 10px 0 0;
    overflow-x: auto;
  }
}

/* 移动布局（D-25）：顶栏只留 logo + EN/中文 + 菜单按钮，主导航收进抽屉 */
@media screen and (max-width: 768px) {
  .header-inner {
    height: 56px;
    padding: 0 24px;
  }

  .brand-logo {
    width: 120px;
    height: 34px;
  }

  .main-nav,
  .register-button,
  .signin-button {
    display: none;
  }

  .nav-toggle {
    display: flex;
  }

  .language-toggle {
    margin-right: 0;
  }

  .breadcrumb-box {
    width: auto;
    max-width: 100%;
    padding: 18px 24px 30px;
  }
}
</style>
